import { useEffect } from "react";
import {
  Outlet,
  createFileRoute,
  useRouterState,
} from "@tanstack/react-router";
import { Page } from "@sx/ui";

import { registerWorkspaceHeader } from "../../../lib/nav";
import { useMe } from "../../../lib/session";
import { RouteFallback } from "../../../shell/RouteFallback";
import { settingsNavItems } from "../../../workspaces/settings/nav";

export const Route = createFileRoute("/_console/settings")({
  component: SettingsLayout,
});

/* Settings' layout, and it is deliberately the thinnest one in the tree.
 *
 * There is no gate here. Every other area asks its own door "what does this identity hold
 * *here*", because a pillar door can refuse a workspace outright. This area's door is the
 * one that already answered "who are you" above it — and the answer to "may I change my own
 * password" is yes, for everyone signed in. The organization screens ask more of the caller,
 * and each refusal is reported where it happens, on the card that asked. Replacing the whole
 * area with a denial would hide the account half, which is nobody's to be denied.
 *
 * The operator screens under `/settings/platform` are the exception, and they carry their
 * own gate: their nav items are absent without `(auth, admin)`, and typing the address
 * anyway lands on a denial rather than a screen that 403s card by card.
 */
function SettingsLayout() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const reading =
    ["/settings", "/settings/keys", "/settings/sessions"].includes(pathname) ||
    /^\/settings\/projects\/[^/]+\/?$/.test(pathname);
  // Already resolved by the shell above; this reads it to decide whether the operator
  // items exist at all. Registering is what gates them, so a grant change re-registers.
  const me = useMe();
  const session = me.data;
  useEffect(() => {
    if (!session) return;
    return registerWorkspaceHeader({
      workspace: "settings",
      items: settingsNavItems(session),
    });
  }, [session]);

  if (!session) return <RouteFallback />;
  return (
    <Page layout={reading ? "reading" : "page"}>
      <Outlet />
    </Page>
  );
}
